import type { ArtikelMatchPosition, KonsolidiertePosition } from "@bidi-handel/core/schemas";

/**
 * Baut aus den OZ-Strings der Match-Positionen (z.B. "01.0010", "02.01.0020")
 * einen sortierten Hierarchie-Baum. Wir gehen davon aus dass der LETZTE
 * Punkt-Block die Position-Nummer ist und alle Blocks davor die Hierarchie
 * darstellen. Beispiele:
 *   "01.0010"      → Hierarchie ["01"],              Position-Nr "0010"
 *   "02.01.0020"   → Hierarchie ["02", "02.01"],     Position-Nr "0020"
 *
 * Pfade werden kumulativ gebildet ("02" → "02.01"), damit Gruppen auf
 * jeder Ebene eindeutig sind. Sortierung: lexikographisch über die
 * vollständige OZ — bei numerisch-padded Blöcken (immer 4-stellig) ist
 * lexikographisch identisch zu numerisch.
 */

export interface PositionWithKonsol {
  match: ArtikelMatchPosition;
  konsol: KonsolidiertePosition | null;
}

export interface OzTreeNode {
  /** Voller Pfad, z.B. "02" oder "02.01". Eindeutig im Baum. */
  path: string;
  /** Anzeige-Label dieser Hierarchie-Ebene, z.B. "02" oder "01" (nur
   *  letzter Block). */
  label: string;
  /** Tiefe ab 0 (Top-Level = 0). */
  depth: number;
  /** Untergruppen unter diesem Knoten. */
  children: OzTreeNode[];
  /** Positionen die DIREKT auf dieser Hierarchie-Ebene hängen (also
   *  z.B. "01.0010" hängt unter Pfad "01"). */
  positions: PositionWithKonsol[];
}

export function buildOzTree(
  positionen: ArtikelMatchPosition[],
  konsolByPositionId: Map<string, KonsolidiertePosition>
): OzTreeNode[] {
  // Map von Pfad -> Node fuer schnelles Auffinden / Erzeugen.
  const nodes = new Map<string, OzTreeNode>();
  const roots: OzTreeNode[] = [];

  function ensureNode(path: string, depth: number): OzTreeNode {
    const existing = nodes.get(path);
    if (existing) return existing;
    const blocks = path.split(".");
    const label = blocks[blocks.length - 1] ?? path;
    const node: OzTreeNode = {
      path,
      label,
      depth,
      children: [],
      positions: [],
    };
    nodes.set(path, node);
    if (depth === 0) {
      roots.push(node);
    } else {
      const parentPath = blocks.slice(0, -1).join(".");
      const parent = ensureNode(parentPath, depth - 1);
      parent.children.push(node);
    }
    return node;
  }

  for (const pos of positionen) {
    const blocks = pos.oz.split(".");
    if (blocks.length < 2) {
      // OZ ohne Hierarchie (nur Position-Nr) → unter virtuelle Top-Gruppe ""
      const node = ensureNode("", 0);
      node.positions.push({
        match: pos,
        konsol: konsolByPositionId.get(pos.positionId) ?? null,
      });
      continue;
    }
    const hierarchyBlocks = blocks.slice(0, -1);
    const parentPath = hierarchyBlocks.join(".");
    const parentDepth = hierarchyBlocks.length - 1;
    const parent = ensureNode(parentPath, parentDepth);
    // Sicherstellen dass alle Zwischen-Pfade existieren (ensureNode
    // erledigt das schon rekursiv ueber parent-Aufrufe).
    parent.positions.push({
      match: pos,
      konsol: konsolByPositionId.get(pos.positionId) ?? null,
    });
  }

  // Sortierung: pro Ebene lexikographisch über Pfad. Positionen pro Knoten
  // ebenfalls über OZ-String.
  function sortRec(list: OzTreeNode[]): void {
    list.sort((a, b) => a.path.localeCompare(b.path));
    for (const n of list) {
      n.positions.sort((a, b) => a.match.oz.localeCompare(b.match.oz));
      sortRec(n.children);
    }
  }
  sortRec(roots);

  return roots;
}

/** Zaehlt rekursiv die Positionen unter diesem Knoten + seinen Kindern. */
export function countPositions(node: OzTreeNode): number {
  let total = node.positions.length;
  for (const c of node.children) total += countPositions(c);
  return total;
}

/** Zaehlt rekursiv die Positionen mit gewaehltem Artikel. */
export function countPicked(node: OzTreeNode): number {
  let total = node.positions.filter((p) => p.match.gewaehlterArtikel).length;
  for (const c of node.children) total += countPicked(c);
  return total;
}

/**
 * Generischer OZ-Baum-Builder für beliebige Items, die ein `oz`-Feld haben.
 * Wird vom LvTreeModal (KonsolidiertePosition) und vom Preise-Tab (PreisPosition
 * + KonsolidiertePosition-Lookup für OZ) wiederverwendet.
 *
 * Optional kann pro Item ein `hauptgruppe`-String ausgelesen werden — wir
 * legen den ersten gefundenen Wert auf jedem Knoten ab, sodass das Frontend
 * den Hauptgruppen-Namen (statt nur OZ) als Header zeigen kann.
 */
export interface OzGroupNode<T> {
  /** Voller Pfad, z.B. "02" oder "02.01". */
  path: string;
  label: string;
  depth: number;
  /** Untergruppen. */
  children: OzGroupNode<T>[];
  /** Items, die DIREKT auf dieser Ebene hängen. */
  items: T[];
  /** Erster Hauptgruppen-Eintrag, der für eine Position dieser Gruppe gesetzt war. */
  hauptgruppe?: string;
}

export function buildOzGroups<T>(
  items: T[],
  getOz: (item: T) => string,
  getHauptgruppe?: (item: T) => string | undefined
): OzGroupNode<T>[] {
  const nodes = new Map<string, OzGroupNode<T>>();
  const roots: OzGroupNode<T>[] = [];

  function ensureNode(path: string, depth: number): OzGroupNode<T> {
    const existing = nodes.get(path);
    if (existing) return existing;
    const blocks = path.split(".");
    const label = blocks[blocks.length - 1] ?? path;
    const node: OzGroupNode<T> = {
      path,
      label,
      depth,
      children: [],
      items: [],
    };
    nodes.set(path, node);
    if (depth === 0) {
      roots.push(node);
    } else {
      const parentPath = blocks.slice(0, -1).join(".");
      const parent = ensureNode(parentPath, depth - 1);
      parent.children.push(node);
    }
    return node;
  }

  function setHauptgruppeUp(path: string, name: string): void {
    // Propagiere den Hauptgruppen-Namen aus der ersten Position bis zur
    // Wurzel — der Top-Level-Knoten bekommt den Anfang des Pfads (z.B.
    // "Erdarbeiten") und tiefere Knoten den Sub-Pfad ("Erdarbeiten · Aushub").
    const blocks = path.split(".");
    const labelParts = name.split(" · ");
    for (let i = 0; i < blocks.length; i++) {
      const subPath = blocks.slice(0, i + 1).join(".");
      const node = nodes.get(subPath);
      if (!node || node.hauptgruppe) continue;
      const sub = labelParts.slice(0, i + 1).join(" · ");
      if (sub) node.hauptgruppe = sub;
    }
  }

  for (const item of items) {
    const oz = getOz(item);
    const blocks = oz.split(".");
    if (blocks.length < 2) {
      const node = ensureNode("", 0);
      node.items.push(item);
      continue;
    }
    const hierarchyBlocks = blocks.slice(0, -1);
    const parentPath = hierarchyBlocks.join(".");
    const parentDepth = hierarchyBlocks.length - 1;
    const parent = ensureNode(parentPath, parentDepth);
    parent.items.push(item);

    if (getHauptgruppe) {
      const hg = getHauptgruppe(item);
      if (hg) setHauptgruppeUp(parentPath, hg);
    }
  }

  function sortRec(list: OzGroupNode<T>[]): void {
    list.sort((a, b) => a.path.localeCompare(b.path));
    for (const n of list) {
      n.items.sort((a, b) => getOz(a).localeCompare(getOz(b)));
      sortRec(n.children);
    }
  }
  sortRec(roots);

  return roots;
}

/** Anzahl Items unter diesem Knoten + Kindern. */
export function countItems<T>(node: OzGroupNode<T>): number {
  let total = node.items.length;
  for (const c of node.children) total += countItems(c);
  return total;
}
